/**
 * An agent's card — Spec 027, PR 1. The header says what the agent is for and where my
 * computer stands with it: a version not approved by me waits for «Одобрить», with what
 * changed since the one I approved (D4). Below, one block at a time: the setup, the process
 * scheme, the helpers, the settings a person changes by hand, and the history.
 *
 * PR 2: where the agent runs — whose computer, which chat, the last action — with «Остановить»,
 * and «Запустить», which gives a one-time issue number and, over a live run, the takeover
 * offer (D8, D11).
 *
 * PR 4: the scheme drawn and edited step by step, and the launch folder with its command for
 * this computer (D3, D5). Spec 042: a staff member's helpers are cards of their own under it —
 * the «Помощники» tab lists them and adds one; a helper's card says whose it is. A background
 * helper is started by the staff member in its chat; one in its own chat launches from its own
 * card and sits under the staff member's run; one «Одобрить» on the staff member covers them.
 *
 * Spec 037: the setup is the job description — its four sections, and what it lacks; «Уроки» is
 * the agent's journal of lessons.
 *
 * Spec 040: «Лента» — what the staff member does wherever it runs, what it waits for and
 * «Написать сотруднику»; it opens first while the staff member works. «Консоль» — its live
 * console from the PC, typed into by a person.
 */

import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { Departments } from "@/api/units";
import { AgentRuns, Agents, Devices } from "@/api/resources";
import type {
  AgentActiveRun,
  AgentDetailOut,
  AgentFeedbackOut,
  AgentLaunchOut,
  AgentRunOut,
  AgentSchemeStep,
  AgentVersionOut,
  AgentWorkItem,
  AgentWorkStats,
  DeviceCommandOut,
  DeviceOut,
} from "@/api/types";
import { AgentFeed } from "@/components/AgentFeed";
import { ApproveSheet, DiffView, NewHelperSheet, SettingsSheet, SetupSheet } from "@/components/AgentForm";
import { AgentLessons } from "@/components/AgentLessons";
import { AccountsBlock } from "@/components/StaffAccounts";
import { AgentPcCommands, CommandFacts, useAgentCommands } from "@/components/Devices";
import { SchemeView, StepSheet } from "@/components/AgentScheme";
import { LiveConsole } from "@/components/LiveConsole";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { AppBar, ago, blockPanel, BlockTabs, Caption, EmptyNote, Pill, whoDid, type BlockTab } from "@/kit";
import {
  ACTION_LABEL,
  approvalLine,
  approvalNeed,
  commandFor,
  FEEDBACK_STATE,
  HELPER_MODE_LABEL,
  helperLine,
  lastAction,
  limitsLine,
  minutesWords,
  MODE_LABEL,
  partLabel,
  runWhere,
  STEP_STATUS,
  STOP_REASON,
  whoLabel,
} from "@/lib/agents";
import { deviceLine, heldCommand, launchTargets, processLine, stopsOf, takeoverNote } from "@/lib/devices";
import { missingLine, SECTIONS } from "@/lib/lessons";
import { Markdown } from "@/lib/markdown";
import { useBackButton } from "@/lib/telegram-ui";
import { pathLabel, placeOf } from "@/lib/units";

type Block = "feed" | "console" | "setup" | "scheme" | "helpers" | "settings" | "lessons" | "accounts" | "work" | "runs" | "history";
const BLOCKS: Block[] = ["feed", "console", "setup", "scheme", "helpers", "settings", "lessons", "accounts", "work", "runs", "history"];
// Spec 037: what the server says of the setup as an instruction, and the active lessons.
type WithLessons = AgentDetailOut & { instruction_missing?: string[]; lessons?: number };

export function AgentScreenRoute() {
  useBackButton("/departments");
  const { agent_id } = useParams<{ agent_id: string }>();
  return agent_id ? <AgentScreen agentId={agent_id} /> : null;
}

/** `embedded`: in the right pane of «Агенты» on a computer — no way back of its own. */
export function AgentScreen({ agentId, embedded = false }: { agentId: string; embedded?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const one = useQuery({ queryKey: ["agents", "one", agentId], queryFn: () => Agents.get(agentId) });
  // Spec 035 v0.4: the staff member's place in the tree; without one it is not launched.
  const units = useQuery({ queryKey: ["departments", "list", false], queryFn: () => Departments.list() });
  const place = units.data ? placeOf(units.data, agentId) : undefined;
  const [sheet, setSheet] = useState<"setup" | "settings" | "approve" | "launch" | "helper" | null>(null);
  const [archiving, setArchiving] = useState(false);
  const navigate = useNavigate();
  // Spec 042: one «Одобрить» on the staff member approves its helpers' current versions too.
  const approveHelpers = useMutation({
    mutationFn: () => Agents.approve(agentId, one.data!.version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agents"] });
      toast.success("Помощники одобрены для твоего компьютера");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const archive = useMutation({
    mutationFn: (to: boolean) => (to ? Agents.archive(agentId) : Agents.restore(agentId)),
    onSuccess: (_, to) => {
      void qc.invalidateQueries({ queryKey: ["agents"] });
      setArchiving(false);
      toast.success(to ? "Агент в архиве" : "Агент вернулся из архива");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const fromUrl = params.get("block");
  // Spec 040: a working staff member opens on its feed; one that does not — on its setup.
  const fallback: Block = one.data?.active_run ? "feed" : "setup";
  const block: Block = fromUrl && (BLOCKS as string[]).includes(fromUrl) ? (fromUrl as Block) : fallback;
  const choose = (key: Block) => {
    const next = new URLSearchParams(params);
    next.set("block", key);
    setParams(next, { replace: true });
  };

  const bar = <AppBar fallback="/departments" context="Отделы" back={!embedded} />;
  if (one.isLoading) return <SkeletonList count={3} />;
  if (one.isError || !one.data) {
    const gone = one.error instanceof ApiError && one.error.status === 404;
    return (
      <div className="flex flex-col gap-3">
        {bar}
        <EmptyNote action={gone ? undefined : <Button size="compact" variant="secondary" onClick={() => void one.refetch()}>Повторить</Button>}>
          {gone ? "Агент не найден." : "Не удалось загрузить агента."}
        </EmptyNote>
      </div>
    );
  }

  const a = one.data as WithLessons;
  const line = approvalLine(a);
  const live = !a.archived_at;
  const need = approvalNeed(a);
  const tabs: BlockTab<Block>[] = [
    { key: "feed", label: "Лента" },
    { key: "console", label: "Консоль" },
    { key: "setup", label: "Установка" },
    { key: "scheme", label: "Схема", count: a.card.scheme.length },
    // A helper has no helpers of its own.
    { key: "helpers", label: "Помощники", count: (a.helpers ?? []).filter((h) => !h.archived_at).length, hidden: Boolean(a.parent_id) },
    { key: "settings", label: "Настройки" },
    { key: "lessons", label: "Уроки", count: a.lessons ?? 0 },
    // Spec 049: a staff member with its own emulator has accounts to sign in with.
    { key: "accounts", label: "Аккаунты", hidden: !((a.card.rights as { device?: string[] }).device ?? []).includes("android") },
    { key: "work", label: "Работа" },
    { key: "runs", label: "Запуски" },
    { key: "history", label: "Версии", count: a.version },
  ];

  return (
    <div className="flex flex-col gap-3">
      {bar}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Pill tone={line.tone}>{line.text}</Pill>
        <span className="text-meta text-p-text-muted">создал {a.created_by.name} · {ago(a.created_at)}</span>
      </div>
      <div>
        <h1 className="break-words text-[21px] font-bold leading-[27px] text-p-text lg:text-[26px] lg:leading-8">{a.name}</h1>
        {a.purpose_md && <Markdown className="mt-1 text-body text-p-text-muted" text={a.purpose_md} />}
      </div>
      {a.parent_id && (
        <p className="text-meta text-p-text-muted" data-helper-of>
          Помощник сотрудника <Link className="font-semibold text-p-link" to={`/agents/${a.parent_id}`}>{a.parent_name ?? "…"}</Link>
          {" · "}{HELPER_MODE_LABEL[a.helper_mode ?? "background"]}
        </p>
      )}
      {place !== undefined && (
        <p className={place ? "text-meta text-p-text-muted" : "text-meta text-p-needs"}>
          {place ? (
            <>
              {place.helperOf ? "Отдел сотрудника: " : place.head ? "Руководитель: " : "Сотрудник: "}
              <Link className="font-semibold text-p-link" to={`/departments/${place.unit.id}`}>{pathLabel(place.unit)}</Link>
            </>
          ) : a.parent_id ? (
            "Без отдела — сотрудника ещё не поставили в подразделение в разделе «Отделы»."
          ) : (
            "Без отдела — не запускается, пока его не поставят в подразделение в разделе «Отделы»."
          )}
        </p>
      )}
      <Approvals a={a} />
      {need === "version" && (
        <div className="space-y-2 rounded-card border border-p-border bg-p-surface px-4 py-3">
          <p className="text-body text-p-text">
            {a.my_approved_version == null
              ? "Твой компьютер запустит этого агента только после твоего одобрения."
              : `С версии ${a.my_approved_version}, которую ты одобрял, карточка изменилась.`}
            {!a.parent_id && (a.helpers ?? []).length > 0 && " Одна кнопка одобряет и текущие версии помощников."}
          </p>
          <Button variant="strong" onClick={() => setSheet("approve")}>Посмотреть и одобрить версию {a.version}</Button>
        </div>
      )}
      {need === "helpers" && (
        <div className="space-y-2 rounded-card border border-p-border bg-p-surface px-4 py-3" data-helpers-approval>
          <p className="text-body text-p-text">
            Изменились помощники: {(a.helpers ?? []).filter((h) => !h.archived_at && h.my_approved_version !== h.version).map((h) => `«${h.name}» — версия ${h.version}`).join(", ")}. Запуск ждёт твоего одобрения; что изменилось — в их карточках.
          </p>
          <Button variant="strong" loading={approveHelpers.isPending} onClick={() => approveHelpers.mutate()}>Одобрить помощников</Button>
        </div>
      )}
      {live && <LiveRun run={a.active_run ?? null} />}
      {live && a.helper_mode === "background" && (
        <p className="text-meta text-p-text-muted">Запускает сотрудник в своём чате: отдельной папки у такого помощника нет. Его вопросы получает сотрудник; молчит полчаса — вопрос уходит людям в «Решить».</p>
      )}
      {live && <AgentPcCommands agentId={a.id} onTakeover={() => setSheet("launch")} />}
      {live && (
        <div className="flex flex-wrap gap-2">
          {need === null && a.helper_mode !== "background" && (
            <Button variant="strong" className="flex-1" onClick={() => setSheet("launch")}>Запустить</Button>
          )}
          <Button variant="secondary" className="flex-1" onClick={() => setSheet("setup")}>Изменить установку</Button>
          <Button variant="secondary" className="flex-1" onClick={() => setSheet("settings")}>Настройки</Button>
        </div>
      )}
      {!live && (
        <div>
          <Button variant="strong" loading={archive.isPending} onClick={() => archive.mutate(false)}>Вернуть из архива</Button>
        </div>
      )}

      <BlockTabs label="Блоки агента" id="agent-blocks" tabs={tabs} value={block} onChange={choose} />
      <div {...blockPanel("agent-blocks", block)} className="flex flex-col gap-3 pt-1">
        {block === "feed" && <AgentFeed agentId={a.id} running={Boolean(a.active_run)} />}
        {block === "console" && <LiveConsole agentId={a.id} running={Boolean(a.active_run)} />}
        {block === "setup" && (a.card.setup_md.trim()
          ? <section className="rounded-card border border-p-border bg-p-surface px-4 py-3.5"><Markdown className="text-body text-p-text" text={a.card.setup_md} /></section>
          : <EmptyNote>Установки пока нет: это инструкции, по которым агент работает.</EmptyNote>)}
        {block === "setup" && (
          <>
            {missingLine(a.instruction_missing) ? (
              <p className="text-meta text-p-needs" role="note">
                {missingLine(a.instruction_missing)}. Установка — это должностная инструкция: заголовки {SECTIONS.map((s) => `«${s}»`).join(", ")}; «Инструменты» — когда агент работает своими инструментами.
              </p>
            ) : null}
            <RuntimeGaps gaps={runtimeGaps(a, "claude")} name="Claude Code" />
            <RuntimeGaps gaps={runtimeGaps(a, "codex")} name="Codex" />
          </>
        )}
        {block === "scheme" && <Scheme a={a} editable={live} />}
        {block === "helpers" && !a.parent_id && <Helpers a={a} editable={live} onAdd={() => setSheet("helper")} />}
        {block === "settings" && <Settings a={a} />}
        {block === "lessons" && <AgentLessons agentId={a.id} />}
        {block === "accounts" && <AccountsBlock agentId={a.id} />}
        {block === "work" && <Work a={a} />}
        {block === "runs" && <Runs a={a} />}
        {block === "history" && <History a={a} />}
      </div>

      {live && (
        <div className="flex flex-wrap items-center gap-x-4">
          <button type="button" className="min-h-11 text-meta font-semibold text-p-text-muted" disabled={archive.isPending}
            onClick={() => (archiving ? archive.mutate(true) : setArchiving(true))}>
            {archiving ? "Точно в архив" : "В архив"}
          </button>
          {archiving && (
            <>
              <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setArchiving(false)}>Отмена</button>
              <span className="text-meta text-p-text-muted">Агент уйдёт из списка и не запустится; вернуть можно из архива.</span>
            </>
          )}
        </div>
      )}

      {sheet === "setup" && <SetupSheet agent={a} onClose={() => setSheet(null)} />}
      {sheet === "settings" && <SettingsSheet agent={a} onClose={() => setSheet(null)} />}
      {sheet === "approve" && <ApproveSheet agent={a} onClose={() => setSheet(null)} />}
      {sheet === "launch" && <LaunchSheet agent={a} onClose={() => setSheet(null)} />}
      {sheet === "helper" && (
        <NewHelperSheet parent={a} onClose={() => setSheet(null)} onCreated={(h) => { setSheet(null); navigate(`/agents/${h.id}`); }} />
      )}
    </div>
  );
}

/** Task 0ec420a2: what the card lacks for one runtime — an agent is written for both from the start. */
function runtimeGaps(agent: AgentDetailOut, runtime: "claude" | "codex"): string[] {
  return (agent as AgentDetailOut & { runtime_check?: Record<string, string[]> }).runtime_check?.[runtime] ?? [];
}

function RuntimeGaps({ gaps, name }: { gaps: string[]; name: string }) {
  if (!gaps.length) return null;
  return (
    <div className="space-y-1 rounded-card border border-p-border bg-p-surface-2 px-4 py-3" role="note">
      <p className="text-body font-semibold text-p-text">Для {name} в карточке не хватает:</p>
      <ul className="space-y-0.5 text-meta text-p-text">
        {gaps.map((gap) => <li key={gap}>· {gap}</li>)}
      </ul>
    </div>
  );
}

/** What the token count does not vouch for, in words next to it, not by colour alone (Spec 030). */
export function usageNote(s: Pick<AgentWorkStats, "usage_incomplete" | "usage_unverified">): string {
  if (s.usage_incomplete) {
    return " · данные неполные: расход пришёл не со всех шагов, не обновлялся или прочитан не целиком";
  }
  if (s.usage_unverified) return " · свежесть расхода не подтверждена: папка агента старой версии";
  return "";
}

/** Where the agent runs now, and «Остановить» — from the phone too (D11). The helpers' own
 * chats sit under the staff member's run and stop with it (Spec 042). */
function LiveRun({ run }: { run: AgentActiveRun | null }) {
  if (!run) return <p className="text-meta text-p-text-muted">Сейчас не запущен.</p>;
  const children = run.subagent_runs ?? [];
  return (
    <section className="space-y-3 rounded-card border border-p-border bg-p-surface px-4 py-3" aria-label="Запуск">
      <RunLine run={run} stopNote={children.length ? "Чат сотрудника и чаты его помощников остановятся при следующем вызове." : undefined} />
      {children.map((child) => (
        <div key={child.id} className="border-l-2 border-p-border pl-3">
          <RunLine run={child} />
        </div>
      ))}
    </section>
  );
}

function RunLine({ run, stopNote }: { run: AgentActiveRun; stopNote?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [sure, setSure] = useState(false);
  const stop = useMutation({
    mutationFn: () => AgentRuns.stop(run.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agents"] });
      setSure(false);
      toast.success("Запуск остановлен: его чат узнает об этом при следующем вызове");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <div className="space-y-1">
      <p className="text-body font-semibold text-p-text">{run.subagent ? `Помощник «${run.subagent}» · ` : ""}{runWhere(run)}</p>
      <p className="text-meta text-p-text-muted">Версия {run.version} · {lastAction(run)}</p>
      <div className="flex flex-wrap items-center gap-x-4">
        <button type="button" className="min-h-11 text-meta font-semibold text-p-text" disabled={stop.isPending}
          onClick={() => (sure ? stop.mutate() : setSure(true))}>
          {sure ? "Точно остановить" : "Остановить"}
        </button>
        {sure && (
          <>
            <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setSure(false)}>Отмена</button>
            <span className="text-meta text-p-text-muted">{stopNote ?? "Чат остановится при следующем вызове."}</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * «Запустить»: the folder of the approved version with a one-time issue number, and the one
 * command for this computer (D5). Over a live run it says at once where the run is and how long
 * ago it last acted, and offers «Перехватить» (D8). Spec 042: a helper's own chat launches
 * from the helper's own card, while its staff member runs.
 */
function LaunchSheet({ agent, onClose }: { agent: AgentDetailOut; onClose: () => void }) {
  // Spec 031 A1: on a connected PC of mine first; the folder by hand stays the way without one.
  const devices = useQuery({ queryKey: ["devices"], queryFn: Devices.list });
  const [manual, setManual] = useState(false);
  const targets = launchTargets(devices.data ?? []);
  if (devices.isLoading) {
    return <Sheet open onClose={onClose} title={`Запустить «${agent.name}»`}><SkeletonList count={2} /></Sheet>;
  }
  if (manual || targets.length === 0) {
    return <ManualLaunchSheet agent={agent} onClose={onClose} onDevice={targets.length ? () => setManual(false) : undefined} />;
  }
  return <DeviceLaunchSheet agent={agent} onClose={onClose} devices={targets} onManual={() => setManual(true)} />;
}

/** Spec 042: a helper's own chat starts only while its staff member runs. */
function HelperNote({ agent }: { agent: AgentDetailOut }) {
  if (!agent.parent_id) return null;
  if (agent.parent_running === false) {
    return <p className="text-meta text-p-blocked" role="note">Сначала запусти сотрудника «{agent.parent_name}»: помощник в своём чате работает под его запуском.</p>;
  }
  return <p className="text-meta text-p-text-muted">Помощник в своём чате: работает под запуском сотрудника «{agent.parent_name}» и останавливается с ним.</p>;
}

/**
 * «Запустить на ПК» (Spec 031 A1): the tasker gives my PC a command; its facts show as they come —
 * the PC got it, checked it, made the process, the agent began. A launch already on its way or
 * running holds the agent: the sheet finds it on opening — after a reload too — and shows it
 * instead of a second one, with its stops (review 7f478c78, 7). A takeover is offered only in
 * words that say what it will do: over a live run, or over a launch whose outcome is unknown.
 */
function DeviceLaunchSheet({ agent, onClose, devices, onManual }: {
  agent: AgentDetailOut;
  onClose: () => void;
  devices: DeviceOut[];
  onManual: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [deviceId, setDeviceId] = useState<string | null>(() => (devices.find((d) => d.online) ?? devices[0])?.id ?? null);
  // Task 03d3e54d: Codex starts from the tasker too — the PC's app gets its folder and runs it.
  const [runtime, setRuntime] = useState<"claude" | "codex">("claude");
  // The command this sheet gave, or the one the server answered with instead.
  const [command, setCommand] = useState<DeviceCommandOut | null>(null);
  const [already, setAlready] = useState(false);
  const commands = useAgentCommands(agent.id);
  const rows = commands.data ?? [];
  const live = agent.active_run ?? null;
  const held = heldCommand(rows, null);
  const mine = command ? rows.find((c) => c.id === command.id) ?? command : null;
  const current = mine ?? held;
  const note = mine ? null : takeoverNote(held, live);
  const takeover = !!held || !!live;
  // Nothing holds the agent — «Запустить»; something does — only an explicit, explained takeover.
  const offer = !mine && (!takeover || note !== null);
  const launch = useMutation({
    mutationFn: (over: boolean) => Devices.launch(deviceId as string, agent.id, over, runtime),
    onSuccess: (c) => {
      setCommand(c);
      setAlready(false);
      void qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (e: Error) => {
      const info = e instanceof ApiError ? e.info : undefined;
      if (info?.error === "launch_on_its_way" && info.command) {
        setCommand(info.command as DeviceCommandOut);
        setAlready(true);
        void qc.invalidateQueries({ queryKey: ["agents"] });
        return;
      }
      toast.error(e.message);
    },
  });
  const changed = () => void qc.invalidateQueries({ queryKey: ["agents"] });
  return (
    <Sheet open onClose={onClose} title={`Запустить «${agent.name}»`}
      footer={offer ? (
        <Button block variant="strong" disabled={!deviceId || commands.isLoading} loading={launch.isPending} onClick={() => launch.mutate(takeover)}>
          {takeover ? "Перехватить на этом ПК" : "Запустить на ПК"}
        </Button>
      ) : undefined}>
      <div className="flex flex-col gap-3">
        <HelperNote agent={agent} />
        {current && (
          <>
            {(already || !mine) && (
              <p className="text-body text-p-text" role="status">Агент уже запускается или работает на ПК «{current.device_name}» — его команда:</p>
            )}
            <CommandFacts command={current} onChanged={changed} />
            {stopsOf(rows, current.id).map((s) => <CommandFacts key={s.id} command={s} onChanged={changed} />)}
          </>
        )}
        {offer && live && (
          <div className="space-y-1 rounded-card border border-p-border bg-p-surface-2 px-4 py-3">
            <p className="text-body text-p-text">{runWhere(live)}.</p>
            <p className="text-meta text-p-text-muted">{lastAction(live)}. {note}</p>
          </div>
        )}
        {offer && !live && note && (
          <p className="rounded-card border border-p-border bg-p-surface-2 px-4 py-3 text-meta text-p-text" role="note">{note}</p>
        )}
        {offer && (
          <fieldset disabled={launch.isPending} className="space-y-2">
            <legend className="text-body font-semibold text-p-text">Где запустить</legend>
            {devices.map((d) => {
              const line = deviceLine(d);
              return (
                <label key={d.id} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-card border border-p-border px-4 py-2 text-body text-p-text">
                  <input type="radio" name="agent-device" value={d.id} checked={deviceId === d.id} className="h-4 w-4 focus-visible:ring-2 focus-visible:ring-p-link" onChange={() => setDeviceId(d.id)} />
                  <span className="flex-1">{d.name}</span>
                  <Pill tone={line.tone}>{line.text}</Pill>
                </label>
              );
            })}
            <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={onManual}>Вручную в терминале — папка и команда</button>
          </fieldset>
        )}
        {offer && (
          <fieldset disabled={launch.isPending} className="space-y-2">
            <legend className="text-body font-semibold text-p-text">Чем запустить</legend>
            {([["claude", "Claude Code"], ["codex", "Codex · пробный режим"]] as const).map(([value, label]) => (
              <label key={value} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-card border border-p-border px-4 py-2 text-body text-p-text">
                <input type="radio" name="agent-device-runtime" value={value} checked={runtime === value} className="h-4 w-4 focus-visible:ring-2 focus-visible:ring-p-link" onChange={() => setRuntime(value)} />
                {label}
              </label>
            ))}
          </fieldset>
        )}
        {offer && <RuntimeGaps gaps={runtimeGaps(agent, runtime)} name={runtime === "codex" ? "Codex" : "Claude Code"} />}
        {offer && runtime === "codex" && (
          <p className="text-meta text-p-text-muted">
            Нужны Codex (подойдёт приложение Codex) и Python 3.11+. В первый раз в окне «Агенты» на ПК Codex сам попросит войти в ChatGPT, подключить «Агенты» через бота и разрешить песочницу Windows; затем одобри хуки в /hooks. Дальше — без этих шагов.
          </p>
        )}
        {offer && <p className="text-meta text-p-text-muted">Версия {agent.version}, одобрена тобой.</p>}
      </div>
    </Sheet>
  );
}

/**
 * «Запустить» by hand: the folder of the approved version with a one-time issue number, and the
 * one command for this computer (D5). Over a live run it says at once where the run is and how
 * long ago it last acted, and offers «Перехватить» (D8).
 */
function ManualLaunchSheet({ agent, onClose, onDevice }: { agent: AgentDetailOut; onClose: () => void; onDevice?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [copied, setCopied] = useState("");
  const [runtime, setRuntime] = useState<"claude" | "codex">("claude");
  const issue = useMutation({
    mutationFn: (takeover: boolean) => Agents.launch(agent.id, takeover, runtime),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["agents", "runs", agent.id] }),
    onError: (e: Error) => toast.error(e.message),
  });
  const download = useMutation({
    mutationFn: (out: AgentLaunchOut) => Agents.downloadFolder(out),
    onError: (e: Error) =>
      toast.error(e instanceof ApiError && (e.status === 409 || e.status === 410)
        ? "Папка устарела: карточка изменилась или номер истёк. Получи папку заново."
        : e.message),
  });
  const out: AgentLaunchOut | undefined = issue.data;
  const live = out?.active_run ?? agent.active_run ?? null;
  const command = out ? commandFor(out.commands, navigator.platform || navigator.userAgent) : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied("Команда скопирована.");
    } catch {
      setCopied("Выдели команду и скопируй вручную.");
    }
  };
  return (
    <Sheet open onClose={onClose} title={`Запустить «${agent.name}»`}
      footer={
        !out ? (
          <Button block variant="strong" loading={issue.isPending} onClick={() => issue.mutate(false)}>
            Получить папку запуска
          </Button>
        ) : live && !out.takeover ? (
          <Button block variant="secondary" loading={issue.isPending} onClick={() => issue.mutate(true)}>
            Перехватить
          </Button>
        ) : undefined
      }>
      <div className="flex flex-col gap-3">
        {live && (
          <div className="space-y-1 rounded-card border border-p-border bg-p-surface-2 px-4 py-3">
            <p className="text-body text-p-text">{runWhere(live)}.</p>
            <p className="text-meta text-p-text-muted">{lastAction(live)}. Если тот чат закрыт — перехвати: он остановится при следующем вызове.</p>
          </div>
        )}
        <HelperNote agent={agent} />
        {!out && (
          <>
          <fieldset disabled={issue.isPending} className="space-y-2">
            <legend className="text-body font-semibold text-p-text">Где запустить</legend>
            {([ ["claude", "Claude Code"], ["codex", "Codex CLI · пробный режим"] ] as const).map(([value, label]) => (
              <label key={value} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-card border border-p-border px-4 py-2 text-body text-p-text">
                <input type="radio" name="agent-runtime" value={value} checked={runtime === value} className="h-4 w-4 focus-visible:ring-2 focus-visible:ring-p-link" onChange={() => setRuntime(value)} />
                {label}
              </label>
            ))}
          </fieldset>
          <RuntimeGaps gaps={runtimeGaps(agent, runtime)} name={runtime === "codex" ? "Codex" : "Claude Code"} />
          {runtime === "codex" && <p className="text-meta text-p-text-muted">Полный запуск на Windows ещё проверяется. Нужны Codex CLI и Python 3.11+. Подойдёт терминал приложения Codex. Модель — из поля «Модель Codex» в настройках карточки, у помощников — из их карточек; пустое поле — модель Codex по умолчанию.</p>}
          <p className="text-body text-p-text-muted">
            Запуск — в терминале, через подключение «Агенты» этого компьютера (адрес — в профиле, «Подключения агентов»). Версия {agent.version}, одобрена тобой.
          </p>
          {onDevice
            ? <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" onClick={onDevice}>На подключённом ПК, без терминала</button>
            : <p className="text-meta text-p-text-muted">Без терминала — с подключённого ПК: подключи его в «Отделах» → «Компьютеры».</p>}
          </>
        )}
        {out && (
          <>
            <p className="text-body text-p-text">
              {out.runtime === "codex" ? "Codex CLI · пробный режим · " : "Claude Code · "}{out.takeover ? "Перехват: версия" : "Версия"} {out.version} · номер выпуска <span className="font-mono font-semibold">{out.issue}</span>
            </p>
            <div>
              <Button variant="strong" loading={download.isPending} onClick={() => download.mutate(out)}>Скачать папку</Button>
            </div>
            <ol className="list-decimal space-y-1.5 pl-5 text-body text-p-text">
              <li>Распакуй <span className="break-all font-mono text-[13px]">{out.folder_name}</span> и открой папку в терминале.</li>
              {out.runtime === "codex" && <li>В первый раз <code className="font-mono text-[13px]">{command}</code> сам попросит войти в ChatGPT, подключить «Агенты» через бота и, в Windows, разрешить песочницу Codex — подтверди запрос администратора. По отдельности: <code className="font-mono text-[13px]">{command} login</code>, <code className="font-mono text-[13px]">{command} mcp-login</code>, <code className="font-mono text-[13px]">{command} sandbox-setup</code>.</li>}
              <li>
                Запусти <code className="select-all rounded-md border border-p-border bg-p-surface-2 px-1.5 py-0.5 font-mono text-[13px]">{command}</code>
                <button type="button" className="ml-2 min-h-11 text-meta font-semibold text-p-link" onClick={() => void copy()}>Скопировать</button>
                <span className="ml-2 text-meta text-p-text-muted" role="status">{copied}</span>
              </li>
              {out.runtime === "codex" ? <li>Проверь /status: workspace-write. Открой /hooks, прочитай и одобри хуки. Только затем вставь сообщение из ready-message.txt. Подробности и путь к CLI — в README.txt.</li> : <li>В первый раз Claude Code попросит войти — это отдельный профиль для агентов, вход один на компьютер. Потом в чате набери /mcp и подключи prosto-agents.</li>}
            </ol>
            {out.skills_missing.length > 0 && (
              <p className="text-meta text-p-text-muted">Без навыков: {out.skills_missing.join(", ")} — их файлов нет на сервере.</p>
            )}
            <p className="text-meta text-p-text-muted">Номер одноразовый и действует сутки.{out.takeover ? " Он перехватит только этот запуск." : ""} Ключей и паролей в папке нет.</p>
          </>
        )}
      </div>
    </Sheet>
  );
}

/**
 * The agent's work as data (D10) and its numbers (D12): items and their steps, who did each,
 * how often a step was returned, time, tokens with the helpers' share, services' units.
 */
function Work({ a }: { a: AgentDetailOut }) {
  const work = useQuery({ queryKey: ["agents", "work", a.id], queryFn: () => Agents.work(a.id) });
  const said = useQuery({ queryKey: ["agents", "feedback", a.id], queryFn: () => Agents.feedback(a.id) });
  const [wrong, setWrong] = useState<AgentWorkItem | "agent" | null>(null);
  const stats = useQuery({ queryKey: ["agents", "stats", a.id], queryFn: () => Agents.stats(a.id) });
  const s = stats.data;
  const tokens = s && (s.tokens_in != null || s.tokens_out != null) ? (s.tokens_in ?? 0) + (s.tokens_out ?? 0) : null;
  return (
    <div className="flex flex-col gap-3">
      <Feedback rows={said.data ?? []} onGive={() => setWrong("agent")} />
      {wrong && <FeedbackSheet agent={a} item={wrong === "agent" ? null : wrong} onClose={() => setWrong(null)} />}
      {s && (
        <dl className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {[
            ["Сделано", `${s.items_done} · в работе ${s.items_open}${s.items_dropped ? ` · брошено ${s.items_dropped}` : ""}`],
            ["Время на единицу", minutesWords(s.avg_item_minutes)],
            ["Токены", `${tokens == null ? "нет данных" : `${tokens.toLocaleString("ru-RU")}${s.subagent_tokens ? ` · из них помощники ${s.subagent_tokens.toLocaleString("ru-RU")}` : ""}`}${usageNote(s)}`],
            ["Из кэша", s.cache_read == null ? "нет данных" : `${s.cache_read.toLocaleString("ru-RU")} — чат перечитывает контекст; в лимиты не входит`],
            ["Сервисы", s.services.length ? s.services.map((x) => `${x.service}: ${x.units}`).join(" · ") : "не использовались"],
            ["Запусков", String(s.runs)],
          ].map(([k, v]) => (
            <div key={k} className="flex flex-col gap-0.5 border-b border-p-border px-4 py-3 last:border-0">
              <dt className="text-micro font-semibold uppercase tracking-[.08em] text-p-text-muted">{k}</dt>
              <dd className="break-words text-body text-p-text">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {s && s.steps.length > 0 && (
        <section className="space-y-1.5">
          <Caption>По шагам</Caption>
          <ul className="space-y-0.5 text-body text-p-text">
            {s.steps.map((x) => (
              <li key={x.key}>· {x.title}: готово {x.done}{x.returned ? `, возвращали ${x.returned}` : ""}, в среднем {minutesWords(x.avg_minutes)}</li>
            ))}
          </ul>
        </section>
      )}
      {work.isLoading && <SkeletonList count={2} />}
      {work.data && !work.data.length && <EmptyNote>Работы пока нет: её начинает запуск по схеме карточки.</EmptyNote>}
      {work.data && work.data.length > 0 && (
        <ul className="flex flex-col gap-2">
          {work.data.map((item: AgentWorkItem) => (
            <li key={item.id} className="space-y-1.5 rounded-card border border-p-border bg-p-surface px-4 py-3">
              <p className="text-[15px] font-semibold leading-5 text-p-text">{item.title}</p>
              <p className="text-meta text-p-text-muted">
                {item.status === "done" ? `готово ${ago(item.finished_at!)}` : item.status === "dropped" ? "брошено" : `в работе с ${ago(item.created_at)}`} · версия {item.version}
              </p>
              <ol className="space-y-0.5 text-meta text-p-text">
                {item.steps.map((st) => (
                  <li key={st.key}>
                    {st.position}. {st.title} — {whoLabel(st.who)}, {STEP_STATUS[st.status]}
                    {st.returned ? ` · возвращали ${st.returned}` : ""}
                    {st.comment_md && st.status === "returned" ? ` · «${st.comment_md}»` : ""}
                  </li>
                ))}
              </ol>
              {item.result_md && <Markdown className="text-meta text-p-text" text={item.result_md} />}
              <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={() => setWrong(item)}>Не так</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Spec 027, D13: what people said the agent got wrong. The agent answers with a proposal to
 * change its card, and the author decides it in «Решить».
 */
function Feedback({ rows, onGive }: { rows: AgentFeedbackOut[]; onGive: () => void }) {
  return (
    <section className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <Caption>Отзывы</Caption>
        <button type="button" className="min-h-11 text-meta font-semibold text-p-link" onClick={onGive}>Агент делает не так</button>
      </div>
      {rows.length === 0 ? (
        <p className="text-meta text-p-text-muted">Отзывов нет. Скажи, что не так, — агент предложит, как поправить себя.</p>
      ) : (
        <ul className="space-y-1">
          {rows.slice(0, 10).map((f) => (
            <li key={f.id} className="text-meta text-p-text">
              <span className="break-words">«{f.text}»</span>
              <span className="text-p-text-muted"> — {[f.author.name, f.item_title, FEEDBACK_STATE[f.state], ago(f.created_at)].filter(Boolean).join(" · ")}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** «Не так»: words about the agent's work, or about one work item of it. */
function FeedbackSheet({ agent, item, onClose }: { agent: AgentDetailOut; item: AgentWorkItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState("");
  const give = useMutation({
    mutationFn: () => Agents.giveFeedback(agent.id, { text: text.trim(), item_id: item?.id ?? null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agents", "feedback", agent.id] });
      toast.success("Отзыв передан агенту: он предложит правку, она придёт в «Решить»");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Sheet open onClose={onClose} title={item ? `Не так: «${item.title}»` : "Агент делает не так"}
      footer={<Button block variant="strong" loading={give.isPending} disabled={!text.trim()} onClick={() => give.mutate()}>Передать агенту</Button>}>
      <div className="flex flex-col gap-3">
        <TextArea label="Что не так" hint="Своими словами. Агент предложит, что поменять в своей установке или схеме; решаешь ты."
          value={text} rows={5} maxLength={2000} onChange={(e) => setText(e.target.value)} />
      </div>
    </Sheet>
  );
}

/** Where the agent ran, newest first. */
function Runs({ a }: { a: AgentDetailOut }) {
  const runs = useQuery({ queryKey: ["agents", "runs", a.id], queryFn: () => Agents.runs(a.id) });
  if (runs.isLoading) return <SkeletonList count={2} />;
  if (!runs.data?.length) return <EmptyNote>Агента ещё не запускали. «Запустить» даёт номер выпуска для чата в терминале.</EmptyNote>;
  return (
    <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
      {runs.data.map((r: AgentRunOut) => (
        <li key={r.id} className="space-y-0.5 border-b border-p-border px-4 py-3 last:border-0">
          <p className="text-[15px] font-semibold leading-5 text-p-text">{r.subagent ? `Помощник «${r.subagent}» · ` : ""}{runWhere(r)}</p>
          <p className="text-meta text-p-text-muted">
            {[
              `версия ${r.version}`,
              `начат ${ago(r.started_at)}`,
              r.stopped_at ? `остановлен ${ago(r.stopped_at)}${r.stop_reason ? ` — ${STOP_REASON[r.stop_reason]}` : ""}` : lastAction(r),
            ].join(" · ")}
          </p>
          {processLine(r) && <p className="text-meta text-p-text" data-run-process>{processLine(r)}</p>}
        </li>
      ))}
    </ul>
  );
}

/** Who approved which version for their computer. */
function Approvals({ a }: { a: AgentDetailOut }) {
  if (!a.approvals.length) return <p className="text-meta text-p-text-muted">Никто ещё не одобрил версию для своего компьютера.</p>;
  return (
    <p className="text-meta text-p-text-muted">
      Одобрили: {a.approvals.map((x) => `${x.person.name} — версия ${x.version}`).join(", ")}
    </p>
  );
}

/** The scheme drawn from its data (D3); a step opens its form, and the form edits it. */
function Scheme({ a, editable }: { a: AgentDetailOut; editable: boolean }) {
  const [open, setOpen] = useState<AgentSchemeStep | "new" | null>(null);
  return (
    <div className="flex flex-col gap-3">
      {a.card.scheme.length ? (
        <>
          <SchemeView scheme={a.card.scheme} onOpen={setOpen} />
          <p className="text-meta text-p-text-muted">Нажми шаг — откроется его форма: кто делает, что ждёт, что на входе и выходе, как проверить.</p>
        </>
      ) : (
        <EmptyNote>Схемы процесса пока нет: шаги, кто их делает и как они проверяются.</EmptyNote>
      )}
      {editable && (
        <div>
          <Button size="compact" variant="secondary" onClick={() => setOpen("new")}>Добавить шаг</Button>
        </div>
      )}
      {open && <StepSheet agent={a} step={open === "new" ? null : open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/**
 * Spec 042: the staff member's helpers — cards of their own under it, in its unit with it. A
 * background one is started by the staff member in its chat; one in its own chat launches from
 * its own card. The card opens on a tap; «Добавить помощника» makes a new one.
 */
function Helpers({ a, editable, onAdd }: { a: AgentDetailOut; editable: boolean; onAdd: () => void }) {
  const helpers = a.helpers ?? [];
  return (
    <div className="flex flex-col gap-3">
      {!helpers.length ? (
        <EmptyNote>Помощников пока нет. Помощник — отдельная карточка под сотрудником: по умолчанию он работает в чате сотрудника; в своём — только когда нужен другой компьютер, модель или права.</EmptyNote>
      ) : (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {helpers.map((h) => (
            <li key={h.id} className="border-b border-p-border last:border-0">
              <Link to={`/agents/${h.id}`} className="flex w-full flex-col gap-1 px-4 py-3 text-left press-surface">
                <span className="text-[15px] font-semibold leading-5 text-p-text">{h.name}{h.archived_at ? " · в архиве" : ""}</span>
                <span className="text-meta text-p-text-muted">{helperLine(h)}</span>
                {h.purpose_md && <span className="line-clamp-2 text-meta text-p-text">{h.purpose_md}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div>
          <Button size="compact" variant="secondary" onClick={onAdd}>Добавить помощника</Button>
        </div>
      )}
      <p className="text-meta text-p-text-muted">У помощника свои установка, навыки, права, лимиты и уроки; в отдел он ходит за сотрудником. Его вопросы получает сотрудник, который его запустил; молчит полчаса — вопрос уходит людям в «Решить».</p>
    </div>
  );
}

function noteLine(notes: { append: boolean }[]): string {
  if (!notes.length) return "";
  const append = notes.filter((n) => n.append).length;
  return `; заметки по адресу: ${notes.length}${append ? ` (дописывать: ${append})` : ""}`;
}

function Settings({ a }: { a: AgentDetailOut }) {
  const { card } = a;
  const rights = card.rights;
  const where = rights.all_projects ? "во всех проектах" : rights.projects.length ? `в проектах: ${rights.projects.length}` : "ни в одном проекте";
  const rows: [string, string][] = [
    ["Результат", MODE_LABEL[card.result_mode]],
    ["Права", `${rights.actions.length ? rights.actions.map((x) => ACTION_LABEL[x]).join(", ") : "ничего"} — ${where}${rights.read_all ? "; читать всё пространство" : ""}${noteLine(rights.notes ?? [])}${rights.web?.length ? `; сеть: ${rights.web.join(", ")}` : ""}`],
    ["Лимиты", limitsLine(card.limits)],
    ["Навыки", card.skills.length ? card.skills.join(", ") : "нет"],
    ["Модель", [card.model ? `Claude Code: ${card.model}` : "Claude Code: по умолчанию", card.codex_model ? `Codex: ${card.codex_model}` : "Codex: по умолчанию"].join(" · ")],
    ["Чаты", card.chats === 1 ? "один чат" : `${card.chats}`],
  ];
  return (
    <div className="flex flex-col gap-3">
      <dl className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col gap-0.5 border-b border-p-border px-4 py-3 last:border-0">
            <dt className="text-micro font-semibold uppercase tracking-[.08em] text-p-text-muted">{k}</dt>
            <dd className="break-words text-body text-p-text">{v}</dd>
          </div>
        ))}
      </dl>
      <section className="space-y-1.5">
        <Caption>Всегда с «да» человека</Caption>
        <ul className="space-y-0.5 text-body text-p-text">
          {[...a.fixed_minimum, ...card.confirmations].map((item) => <li key={item}>· {item}</li>)}
        </ul>
      </section>
      <p className="text-meta text-p-text-muted">Эти настройки меняет человек — кнопкой «Настройки» или через своего агента, своими словами.</p>
    </div>
  );
}

/** The card's history; a version opens what changed from the one before it. */
function History({ a }: { a: AgentDetailOut }) {
  const versions = useQuery({ queryKey: ["agents", "versions", a.id, a.version], queryFn: () => Agents.versions(a.id) });
  const [open, setOpen] = useState<AgentVersionOut | null>(null);
  const diff = useQuery({
    queryKey: ["agents", "diff", a.id, open ? open.version - 1 : 0, open?.version],
    queryFn: () => Agents.diff(a.id, open!.version - 1, open!.version),
    // Version 1 opens against the empty card: all of it shows.
    enabled: Boolean(open),
  });
  if (versions.isLoading) return <SkeletonList count={2} />;
  if (!versions.data?.length) return <EmptyNote>Истории пока нет.</EmptyNote>;
  return (
    <>
      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        {versions.data.map((v) => (
          <li key={v.version} className="border-b border-p-border last:border-0">
            <button type="button" className="flex w-full flex-col gap-0.5 px-4 py-3 text-left press-surface" onClick={() => setOpen(v)}>
              <span className="text-[15px] font-semibold leading-5 text-p-text">Версия {v.version}{v.version === a.version ? " · текущая" : ""}</span>
              <span className="text-meta text-p-text-muted">
                {[whoDid(v.author.name, v.via_agent), ago(v.created_at), v.version === 1 ? "создан" : v.changed.map(partLabel).join(", "), v.from_request_id ? "по отзыву" : null].filter(Boolean).join(" · ")}
              </span>
              {v.user_confirmation && <span className="break-words text-meta text-p-text">«{v.user_confirmation}»</span>}
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <Sheet open onClose={() => setOpen(null)} title={open.version === 1 ? "Версия 1: карточка целиком" : `Версия ${open.version}: что изменилось`}>
          {diff.isLoading ? <SkeletonList count={2} /> : diff.data ? <DiffView diff={diff.data} /> : <EmptyNote>Не удалось загрузить разницу.</EmptyNote>}
        </Sheet>
      )}
    </>
  );
}
